import { useState, useMemo } from 'react'
import {
  Card,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Spinner,
  Select,
  SelectItem,
} from '@heroui/react'
import { MagnifyingGlassIcon, EyeIcon } from '@heroicons/react/24/outline'

import { useGifs } from '../../gifs/hooks/useGifs'
import {
  UseCaseAnalyticsService,
  type UseCaseData,
  type UseCaseAnalytics,
} from '../services/useCaseAnalyticsService'

import { GifThumbnail } from './GifThumbnail'

interface UseCaseAnalyticsProps {
  className?: string
}

export function UseCaseAnalytics({ className }: UseCaseAnalyticsProps) {
  const { gifs, loading, error } = useGifs()
  const [searchTerm, setSearchTerm] = useState('')
  const [minCountFilter, setMinCountFilter] = useState('1')
  const [selectedUseCase, setSelectedUseCase] =
    useState<UseCaseAnalytics | null>(null)

  const { isOpen, onOpen, onClose } = useDisclosure()

  // Process use case data
  const useCaseData = useMemo((): UseCaseData | null => {
    if (loading || !gifs.length) return null

    let data = UseCaseAnalyticsService.analyzeUseCases(gifs)

    // Apply minimum count filter
    const minCount = parseInt(minCountFilter, 10)
    if (minCount > 1) {
      data = UseCaseAnalyticsService.filterByMinCount(data, minCount)
    }

    // Apply search filter
    if (searchTerm.trim()) {
      data = UseCaseAnalyticsService.searchUseCases(data, searchTerm)
    }

    return data
  }, [gifs, loading, searchTerm, minCountFilter])

  // Get summary statistics
  const summaryStats = useMemo(() => {
    if (!useCaseData) return null
    return UseCaseAnalyticsService.getSummaryStats(useCaseData)
  }, [useCaseData])

  // Get GIFs for selected use case
  const selectedGifs = useMemo(() => {
    if (!selectedUseCase) return []
    return gifs.filter((gif) => selectedUseCase.gifIds.includes(gif.id))
  }, [selectedUseCase, gifs])

  const handleViewGifs = (useCaseAnalytics: UseCaseAnalytics) => {
    setSelectedUseCase(useCaseAnalytics)
    onOpen()
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-12 ${className}`}>
        <div className="text-center">
          <Spinner size="lg" className="mb-4" />
          <p className="text-default-500">Loading use case analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-danger mb-4">Error loading GIF data: {error}</p>
        <Button
          variant="flat"
          color="primary"
          onPress={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    )
  }

  if (!useCaseData) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <p className="text-default-500">No GIF data available</p>
      </div>
    )
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="text-4xl">📊</div>
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            Use Case Analytics
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Analysis of GIF use cases across the entire collection
          </p>
        </div>
      </div>

      {/* Summary Statistics */}
      {summaryStats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {useCaseData.uniqueUseCases}
              </div>
              <div className="text-sm text-default-500">Unique Use Cases</div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-success">
                {useCaseData.totalUseCases}
              </div>
              <div className="text-sm text-default-500">Total Use Cases</div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-warning">
                {summaryStats.averageUseCasesPerGif}
              </div>
              <div className="text-sm text-default-500">Avg per GIF</div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-secondary">
                {summaryStats.averageOccurrences}
              </div>
              <div className="text-sm text-default-500">Avg Occurrences</div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <Input
            placeholder="Search use cases..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            startContent={
              <MagnifyingGlassIcon className="w-4 h-4 text-default-400" />
            }
            className="flex-1"
            isClearable
          />

          <Select
            label="Minimum Count"
            selectedKeys={[minCountFilter]}
            onSelectionChange={(keys) => {
              const selectedKey = Array.from(keys)[0] as string
              setMinCountFilter(selectedKey)
            }}
            className="w-48"
          >
            <SelectItem key="1">1+ occurrences</SelectItem>
            <SelectItem key="2">2+ occurrences</SelectItem>
            <SelectItem key="3">3+ occurrences</SelectItem>
            <SelectItem key="5">5+ occurrences</SelectItem>
            <SelectItem key="10">10+ occurrences</SelectItem>
          </Select>
        </div>
      </Card>

      {/* Results */}
      <Card>
        <Table aria-label="Use case analytics table">
          <TableHeader>
            <TableColumn>USE CASE</TableColumn>
            <TableColumn>COUNT</TableColumn>
            <TableColumn>PERCENTAGE</TableColumn>
            <TableColumn>ACTIONS</TableColumn>
          </TableHeader>
          <TableBody
            items={useCaseData.analytics}
            emptyContent={
              searchTerm || minCountFilter !== '1'
                ? 'No use cases match your filters'
                : 'No use cases found'
            }
          >
            {(item) => (
              <TableRow key={item.useCase}>
                <TableCell>
                  <div className="max-w-md">
                    <span className="font-medium">{item.useCase}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Chip size="sm" variant="flat" color="primary">
                    {item.count}
                  </Chip>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-default-500">
                    {item.percentage.toFixed(1)}%
                  </span>
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="flat"
                    color="secondary"
                    startContent={<EyeIcon className="w-4 h-4" />}
                    onPress={() => handleViewGifs(item)}
                  >
                    View GIFs ({item.count})
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* GIF Preview Modal */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            <div>
              <h3 className="text-lg font-semibold">GIFs for Use Case</h3>
              <p className="text-sm text-default-500 font-normal">
                &ldquo;{selectedUseCase?.useCase}&rdquo;
              </p>
            </div>
          </ModalHeader>
          <ModalBody>
            {selectedGifs.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {selectedGifs.map((gif) => (
                  <div key={gif.id} className="space-y-2">
                    <GifThumbnail gif={gif} className="w-full h-32" />
                    <div className="text-xs text-default-500 text-center">
                      {gif.id.slice(-8)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-default-500">
                No GIFs found for this use case
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
